import { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { api } from '../lib/api';
import Input from '../components/Input';
import Button from '../components/Button';
import TurnstileWidget from '../components/TurnstileWidget';

export default function Register() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleToken = useCallback((token: string) => setTurnstileToken(token), []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError(t('auth.passwordMinLength')); return; }
    if (!turnstileToken) { setError(t('auth.completeVerification')); return; }
    setLoading(true);
    try {
      await api.post('/auth/register', { email, password, turnstileToken });
      navigate('/pricing');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.emailExists'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="max-w-md mx-auto px-6 py-20"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="text-center mb-8">
        <h1 className="font-display text-3xl font-bold mb-2">{t('auth.createAccount')}</h1>
        <p className="text-muted text-sm">{t('auth.joinExusiai')}</p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="bg-danger/10 border border-danger/30 rounded px-4 py-3 text-sm text-danger">{error}</div>
        )}
        <Input label={t('auth.email')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" autoFocus />
        <Input label={t('auth.password')} type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="new-password" placeholder={t('auth.passwordPlaceholder')} />
        <TurnstileWidget onToken={handleToken} />
        <Button type="submit" disabled={loading || !turnstileToken} className="w-full" size="lg">
          {loading ? t('auth.creating') : t('auth.createAccount')}
        </Button>
      </form>
      <p className="text-center text-sm text-muted mt-6">
        {t('auth.alreadyHaveAccount')}{' '}
        <Link to="/login" className="text-primary hover:text-primary-hover">{t('auth.signIn')}</Link>
      </p>
    </motion.div>
  );
}
